import { type AppResourceHint, validAppResource } from '@tasks/bridge-client/host';
import { PROJECT_KEY_PATTERN } from '@tasks/contracts/projects';
import { projectKeyOf } from '#features/work-items/item-navigation';

/**
 * APP-CONTEXT-DESK-1 — what this app's route says the person has OPEN, by reference (never a title): the one fact the host's chat can use to
 * resolve «move this to In progress». The route table of this app (`routeTree.gen.ts`) is the only source: `/item/$ref` is a task,
 * `/$projectKey` and `/$projectKey/board` are a board; every other place (`/my`, `/inbox`, `/settings/*`, `/login`, `/setup`, `/`) has nothing
 * open. The query and hash are ignored: a filter is not a resource. The result goes through the SAME `validAppResource` the host and the door
 * apply (closed `kind`, closed-alphabet `ref`): a key outside it is simply not reported.
 *
 * ONE EXCEPTION, measured against the running app (APP-CONTEXT-DESK-1 QA, 07-10): the list and the board open a task in a DETAIL PANEL, not on
 * `/item/$ref` — `search-schema.ts`: «`item`: Panel de detalle. Es un parámetro, no una ruta». So on `/my`, `/$projectKey` and
 * `/$projectKey/board` a valid `?item=<ref>` IS a task, and it wins over the board behind it (the person is looking at the task). An `item` that
 * does not pass the reference alphabet, or that names ANOTHER project's task (`/RT?item=QA26-2`: the ref's own project key, `projectKeyOf`, is not the
 * route's), falls back to what the path alone says. Any other query key stays ignored.
 */
const FIRST_SEGMENTS_THAT_ARE_NOT_A_PROJECT = new Set([
  'inbox',
  'my',
  'item',
  'settings',
  'login',
  'setup',
]);

const decoded = (segment: string): string | null => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
};

/**
 * `project` = the route's project key, or null for `/my` (no project: the person's own list, any task of any project — but still a REFERENCE:
 * `/my?item=x` is not one, T1 BABAS R1).
 */
const taskOpenInPanel = (
  searchItem: string | null,
  project: string | null,
): AppResourceHint | undefined => {
  if (searchItem === null) return undefined;
  const itemProject = projectKeyOf(searchItem);
  // the key must be a REAL project key (`PROJECT_KEY_PATTERN`): `projectKeyOf` alone would take any `<text>-<digits>`, a uuid ending in digits included
  if (itemProject === null || !PROJECT_KEY_PATTERN.test(itemProject)) return undefined;
  if (project !== null && itemProject !== project) return undefined;
  return validAppResource({ kind: 'task', ref: searchItem }) ?? undefined;
};

export function resourceOfPath(path: string): AppResourceHint | undefined {
  let pathname: string;
  let searchItem: string | null;
  try {
    const url = new URL(path, 'https://app.invalid');
    pathname = url.pathname;
    searchItem = url.searchParams.get('item');
  } catch {
    return undefined;
  }
  const segments = pathname.split('/').filter((s) => s !== '');
  const first = segments[0];
  if (first === undefined) return undefined;
  const key = decoded(first);
  if (key === null) return undefined;
  if (first === 'item') {
    const ref = segments.length === 2 && segments[1] !== undefined ? decoded(segments[1]) : null;
    return ref === null ? undefined : (validAppResource({ kind: 'task', ref }) ?? undefined);
  }
  // `/my` is the one place that is not a project and still has the detail panel
  if (first === 'my') return segments.length === 1 ? taskOpenInPanel(searchItem, null) : undefined;
  if (FIRST_SEGMENTS_THAT_ARE_NOT_A_PROJECT.has(first)) return undefined;
  const isBoard = segments.length === 1 || (segments.length === 2 && segments[1] === 'board');
  if (!isBoard) return undefined;
  return (
    taskOpenInPanel(searchItem, key) ?? validAppResource({ kind: 'board', ref: key }) ?? undefined
  );
}
